import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  FileText,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  IndianRupee,
  CreditCard,
  Building,
  Globe,
  Hash,
  Home,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Download,
  MessageSquare,
  History,
  Eye,
  X,
  Image as ImageIcon,
  RefreshCw,
  Camera,
  Store,
  Package,
  Coins,
  Info
} from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import DashboardLayout from '../dashboard/_components/DashboardLayout';
import { toast } from 'sonner';
import logo from '../../assets/logod.png';

export default function BusinessLoanDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, action: '', status: '' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user && id) {
      const fetchLoan = async () => {
        try {
          const loanDoc = await getDoc(doc(db, 'businessLoans', id));
          if (loanDoc.exists()) {
            setLoan({ id: loanDoc.id, ...loanDoc.data() });
          } else {
            // Silent fail
            navigate('/business-loans');
          }
        } catch (error) {
          // Silent fail
        } finally {
          setLoading(false);
        }
      };
      fetchLoan();
    }
  }, [user, id, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp?.toDate) {
        date = timestamp.toDate();
      } else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      return date.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      'under-review': 'bg-blue-100 text-blue-800 border-blue-200',
      disbursed: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      'under-review': AlertCircle,
      disbursed: DollarSign,
      completed: CheckCircle
    };
    return icons[status] || Clock;
  };

  const handleStatusUpdate = (newStatus) => {
    if (!loan) return;
    
    let actionText = '';
    if (newStatus === 'under-review') {
      actionText = 'move this loan application to review';
    } else if (newStatus === 'approved') {
      actionText = 'approve this loan application';
    } else if (newStatus === 'rejected') {
      actionText = 'reject this loan application';
    }
    
    setConfirmModal({ show: true, action: actionText, status: newStatus });
  };

  const confirmStatusUpdate = async () => {
    const newStatus = confirmModal.status;
    setConfirmModal({ show: false, action: '', status: '' });
    
    setUpdating(true);
    try {
      // Update loan status
      await updateDoc(doc(db, 'businessLoans', id), {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: user.uid
      });
      
      setLoan({ ...loan, status: newStatus, updatedAt: new Date() });
      toast.success(`Loan application ${newStatus.replace('-', ' ')} successfully!`);

      // Send email notification to customer about status change
      try {
        if (loan?.email) {
          const statusEmailTemplates = {
            'under-review': {
              subject: 'Your Business Loan Application is Under Review',
              message: `Dear ${loan.fullName || loan.businessName || 'Valued Customer'},\n\nYour business loan application is now under review. Our team is carefully evaluating your application and will get back to you soon.\n\nLoan Details:\n- Application ID: ${id}\n- Name: ${loan.fullName || loan.businessName || 'N/A'}\n- Loan Amount: ₹${formatCurrency(loan.loanAmount) || 'N/A'}\n\nWe appreciate your patience during this process.\n\nBest regards,\nAadhar Finance Team`,
              color: '#3B82F6'
            },
            'approved': {
              subject: '🎉 Congratulations! Your Business Loan Application is Approved',
              message: `Dear ${loan.fullName || loan.businessName || 'Valued Customer'},\n\nGreat news! Your business loan application has been approved!\n\nLoan Details:\n- Application ID: ${id}\n- Name: ${loan.fullName || loan.businessName || 'N/A'}\n- Loan Amount: ₹${formatCurrency(loan.loanAmount) || 'N/A'}\n\nOur team will contact you shortly to complete the disbursement process.\n\nCongratulations and thank you for choosing Aadhar Finance!\n\nBest regards,\nAadhar Finance Team`,
              color: '#10B981'
            },
            'rejected': {
              subject: 'Update on Your Business Loan Application',
              message: `Dear ${loan.fullName || loan.businessName || 'Valued Customer'},\n\nThank you for your interest in Aadhar Finance. After careful review, we regret to inform you that we are unable to approve your business loan application at this time.\n\nLoan Details:\n- Application ID: ${id}\n- Name: ${loan.fullName || loan.businessName || 'N/A'}\n- Loan Amount: ₹${formatCurrency(loan.loanAmount) || 'N/A'}\n\nWe encourage you to reapply in the future or contact us if you have any questions.\n\nThank you for your understanding.\n\nBest regards,\nAadhar Finance Team`,
              color: '#EF4444'
            }
          };

          const template = statusEmailTemplates[newStatus];
          if (template) {
            const mailData = {
              to: loan.email,
              message: {
                subject: template.subject,
                html: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background: linear-gradient(135deg, ${template.color} 0%, ${template.color}dd 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                      <h1 style="margin: 0; font-size: 24px;">${template.subject}</h1>
                    </div>
                    <div style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                      <div style="background-color: white; padding: 20px; border-radius: 8px; white-space: pre-line; line-height: 1.6;">
                        ${template.message}
                      </div>
                    </div>
                  </div>
                `
              },
              createdAt: new Date(),
              createdBy: user.uid,
              metadata: {
                loanId: id,
                loanType: 'business',
                statusUpdate: newStatus
              }
            };

            await addDoc(collection(db, 'mail'), mailData);
          }
        }
      } catch (emailError) {
        // Silent fail - status update was successful
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadDocuments = () => {
    if (!loan || !loan.documents) {
      toast.error('No documents available to download');
      return;
    }

    const docs = loan.documents;
    const urls = [
      { url: docs.aadharFront, name: 'Aadhar_Front' },
      { url: docs.aadharBack, name: 'Aadhar_Back' },
      { url: docs.panCardFront, name: 'PAN_Front' },
      { url: docs.panCardBack, name: 'PAN_Back' },
      { url: docs.selfie, name: 'Selfie' },
      { url: docs.shopPhoto, name: 'Shop_Photo' },
      { url: docs.lastSixMonthBill, name: 'Last_6M_Bill' },
      { url: docs.gstDocument, name: 'GST_Document' },
      { url: docs.shopStockPhoto, name: 'Shop_Stock_Photo' }
    ];

    urls.forEach(({ url, name }) => {
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${loan.fullName || loan.businessName || 'BusinessLoan'}_${name}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const handleSendMessage = async () => {
    if (!subject.trim()) {
      toast.warning('Please enter a subject');
      return;
    }
    
    if (!message.trim()) {
      toast.warning('Please enter a message');
      return;
    }

    if (!loan.email) {
      toast.error('Customer email not found');
      return;
    }

    setSendingMessage(true);
    try {
      // Add email to Firebase mail collection for processing
      const mailRef = collection(db, 'mail');
      await addDoc(mailRef, {
        to: loan.email,
        message: {
          subject: subject,
          text: message,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2563eb;">${subject}</h2>
              <p style="color: #374151; line-height: 1.6; white-space: pre-wrap;">${message}</p>
              <hr style="border: 1px solid #e5e7eb; margin: 20px 0;" />
              <p style="color: #6b7280; font-size: 12px;">This is an automated message from Aadhar Finance regarding your business loan application.</p>
            </div>
          `
        },
        createdAt: new Date(),
        sentBy: user.email,
        customerId: loan.userId || id,
        customerName: loan.fullName || loan.businessName
      });
      
      toast.success(`Email sent successfully to ${loan.fullName || loan.businessName}!`);
      setMessage('');
      setSubject('');
      setMessageModal(false);
    } catch (error) {
      // Silent fail
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading loan details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!loan) return null;

  // Loan offer summary values (simple interest calculation)
  const FIXED_MONTHLY_INTEREST_RATE = 2.0833;
  const tenureMonths = loan.tenure || loan.loanTenure;
  const monthlyInterestRate = loan.interestRate
    ? Number(loan.interestRate)
    : FIXED_MONTHLY_INTEREST_RATE;
  // Simple interest: charged on the full principal every month
  const monthlyInterest = Math.round(
    (Number(loan.loanAmount) * monthlyInterestRate) / 100
  );

  const InfoCard = ({ icon, label, value, fullWidth = false }) => {
    const Icon = icon;
    return (
      <div className={`${fullWidth ? 'col-span-full' : ''}`}>
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
          <div className="p-2 bg-white rounded-lg shrink-0">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-sm font-semibold text-gray-900 mt-1 break-words">{value || 'N/A'}</p>
          </div>
        </div>
      </div>
    );
  };

  const StatusIcon = getStatusIcon(loan.status);

  return (
    <DashboardLayout>
      {/* Back Button & Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Loan Applications</span>
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Building className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Business Loan Application
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Applied by {loan.fullName || loan.businessName || 'N/A'}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusColor(loan.status)}`}>
                    <StatusIcon className="w-4 h-4" />
                    <span className="capitalize">{loan.status?.replace('-', ' ') || 'Pending'}</span>
                  </span>
                  <span className="px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {formatCurrency(Number(loan.loanAmount))}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {(loan.status?.toLowerCase() === 'pending' || !loan.status) && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('under-review')}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Review
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('rejected')}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
              {loan.status?.toLowerCase() === 'under-review' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('rejected')}
                    disabled={updating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
              {loan.status?.toLowerCase() === 'approved' && (
                <button
                  onClick={() => handleStatusUpdate('disbursed')}
                  disabled={updating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  <DollarSign className="w-4 h-4" />
                  Mark as Disbursed
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Loan Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-blue-600" />
              Loan Details
            </h2>
            {/* Loan offer summary card: terms and arranger fee */}
            {(loan.loanAmount || tenureMonths || loan.arrangerFee) && (
              <div className="mb-5 bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Loan Amount</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(Number(loan.loanAmount))}
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Coins className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 mt-5">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Interest Rate</p>
                      <p className="text-lg font-bold text-gray-900">
                        {`${monthlyInterestRate}% p.m.`}
                      </p>
                    </div>
                    <div className="sm:text-center">
                      <p className="text-sm text-gray-500 mb-1">Tenure</p>
                      <p className="text-lg font-bold text-gray-900">
                        {tenureMonths ? `${tenureMonths} Months` : 'N/A'}
                      </p>
                    </div>
                    <div className="sm:text-center">
                      <p className="text-sm text-gray-500 mb-1">
                        Monthly Interest
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {monthlyInterest ? formatCurrency(monthlyInterest) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 bg-blue-50 flex items-center justify-between gap-3">
                  {loan.arrangerFee !== undefined &&
                  loan.arrangerFee !== null ? (
                    <p className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="truncate">
                        Arranger Fee: {formatCurrency(Number(loan.arrangerFee))}
                      </span>
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
                    Powered by
                    <img
                      src={logo}
                      alt="Aadhar Finance"
                      className="h-5 w-auto object-contain"
                    />
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={IndianRupee} label="Loan Amount Requested" value={formatCurrency(Number(loan.loanAmount))} />
              <InfoCard icon={FileText} label="Loan Purpose" value={loan.loanPurpose} />
              <InfoCard icon={User} label="User ID" value={loan.userId} />
              <InfoCard icon={Calendar} label="Applied Date" value={formatDate(loan.applicationDate)} />
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-600" />
              Business Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={User} label="Full Name" value={loan.fullName || loan.businessName} />
              <InfoCard icon={User} label="Gender" value={loan.gender ? loan.gender.charAt(0).toUpperCase() + loan.gender.slice(1) : 'N/A'} />
              <InfoCard icon={Mail} label="Email Address" value={loan.email} />
              <InfoCard icon={Phone} label="Mobile Number" value={loan.mobileNumber} />
              <InfoCard icon={Calendar} label="Date of Birth" value={loan.dateOfBirth || 'N/A'} />
              <InfoCard icon={CreditCard} label="Aadhar Number" value={loan.aadharNumber} />
              <InfoCard icon={CreditCard} label="PAN Number" value={loan.panNumber} />
              <InfoCard icon={FileText} label="GST Number" value={loan.gstNumber} />
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={Home} label="Complete Address" value={loan.completeAddress || loan.businessAddress} fullWidth />
              <InfoCard icon={Home} label="Address Type" value={loan.addressType || 'N/A'} />
              <InfoCard icon={MapPin} label="City" value={loan.city} />
              <InfoCard icon={Globe} label="State" value={loan.state} />
              <InfoCard icon={Hash} label="Pincode" value={loan.pincode} />
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Documents
              </h2>
              <button
                onClick={() => setShowDocumentModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <Eye className="w-4 h-4" />
                View All Documents
              </button>
            </div>
            <p className="text-sm text-gray-600">This application includes 9 supporting documents: Aadhar (front/back), PAN (front/back), Selfie, Shop Photo, Business Billing, GST Document, and Shop Stock Photo.</p>
          </div>

          {/* Bank Details */}
          {(loan.bankAccountNumber || loan.ifscCode) && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                Bank Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCard icon={Hash} label="Account Number" value={loan.bankAccountNumber} />
                <InfoCard icon={FileText} label="IFSC Code" value={loan.ifscCode} />
                <InfoCard icon={Building} label="Bank Name" value={loan.bankName} />
              </div>
            </div>
          )}


        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase">Applied On</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatDate(loan.applicationDate)}
                  </p>
                </div>
              </div>

              {loan.updatedAt && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-500 uppercase">Last Updated</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {formatDate(loan.updatedAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button 
                onClick={handleDownloadDocuments}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4 text-gray-500" />
                Download Documents
              </button>
              <button 
                onClick={() => setMessageModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <MessageSquare className="w-4 h-4 text-gray-500" />
                Send Message
              </button>
              <button 
                onClick={() => setShowActivityLog(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <History className="w-4 h-4 text-gray-500" />
                View Activity Log
              </button>
              <button 
                onClick={() => setShowDocumentModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <Eye className="w-4 h-4 text-blue-500" />
                View Documents
              </button>
            </div>
          </div>

          {/* Application Summary */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-sm border border-blue-100 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Application Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Loan Amount</span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(Number(loan.loanAmount))}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Loan Purpose</span>
                <span className="text-sm font-bold text-gray-900">{loan.loanPurpose || 'N/A'}</span>
              </div>
              <div className="border-t border-blue-200 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Application Complete</span>
                  <span className="text-sm font-bold text-gray-900">
                    {loan.isApplicationComplete ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Application Status</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${getStatusColor(loan.status)}`}>
                  {loan.status?.replace('-', ' ') || 'Pending'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Document Preview</h3>
                <p className="text-sm text-gray-600 mt-0.5">{loan.fullName || loan.businessName || 'N/A'}</p>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Identity Documents Section */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Identity Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Aadhar Front */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <h5 className="font-semibold text-gray-900">Aadhar Card</h5>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {loan.aadharNumber || 'N/A'}
                      </span>
                    </div>
                    
                    {loan.documents?.aadharFront ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-600">Front Side</p>
                        {loan.documents.aadharFront.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.aadharFront}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.aadharFront}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.aadharFront}
                              alt="Aadhar Front"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Front not available</p>
                      </div>
                    )}

                    {loan.documents?.aadharBack ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-600">Back Side</p>
                        {loan.documents.aadharBack.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.aadharBack}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.aadharBack}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.aadharBack}
                              alt="Aadhar Back"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Back not available</p>
                      </div>
                    )}
                  </div>

                  {/* PAN Card */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-5 h-5 text-green-600" />
                      <h5 className="font-semibold text-gray-900">PAN Card</h5>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {loan.panNumber || 'N/A'}
                      </span>
                    </div>
                    
                    {loan.documents?.panCardFront ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-600">Front Side</p>
                        {loan.documents.panCardFront.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.panCardFront}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-green-600" />
                            <span className="text-sm font-medium text-green-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.panCardFront}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.panCardFront}
                              alt="PAN Front"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Front not available</p>
                      </div>
                    )}

                    {loan.documents?.panCardBack ? (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-600">Back Side</p>
                        {loan.documents.panCardBack.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.panCardBack}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-green-600" />
                            <span className="text-sm font-medium text-green-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.panCardBack}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.panCardBack}
                              alt="PAN Back"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Back not available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Selfie Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-600" />
                  Selfie
                </h4>
                <div className="grid grid-cols-1 gap-6">
                  {/* Selfie */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Camera className="w-5 h-5 text-indigo-600" />
                      <h5 className="font-semibold text-gray-900">Selfie Photo</h5>
                    </div>
                    
                    {loan.documents?.selfie ? (
                      <div className="space-y-2">
                        {loan.documents.selfie.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.selfie}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border-2 border-indigo-200 hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-indigo-600" />
                            <span className="text-sm font-medium text-indigo-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.selfie}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.selfie}
                              alt="Selfie"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Selfie not available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Documents Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-orange-600" />
                  Business Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Shop Photo */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Store className="w-5 h-5 text-orange-600" />
                      <h5 className="font-semibold text-gray-900">Shop Photo</h5>
                    </div>
                    
                    {loan.documents?.shopPhoto ? (
                      <div className="space-y-2">
                        {loan.documents.shopPhoto.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.shopPhoto}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-orange-600" />
                            <span className="text-sm font-medium text-orange-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.shopPhoto}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.shopPhoto}
                              alt="Shop Photo"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Shop Photo not available</p>
                      </div>
                    )}
                  </div>

                  {/* Business Billing */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <h5 className="font-semibold text-gray-900">Last 6 Months Bill</h5>
                    </div>
                    
                    {loan.documents?.lastSixMonthBill ? (
                      <div className="space-y-2">
                        {loan.documents.lastSixMonthBill.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.lastSixMonthBill}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-purple-600" />
                            <span className="text-sm font-medium text-purple-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.lastSixMonthBill}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.lastSixMonthBill}
                              alt="Last 6 Months Bill"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-purple-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Last 6 Months Bill not available</p>
                      </div>
                    )}
                  </div>

                  {/* GST Document */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h5 className="font-semibold text-gray-900">GST Document</h5>
                    </div>
                    
                    {loan.documents?.gstDocument ? (
                      <div className="space-y-2">
                        {loan.documents.gstDocument.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.gstDocument}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.gstDocument}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.gstDocument}
                              alt="GST Document"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">GST Document not available</p>
                      </div>
                    )}
                  </div>

                  {/* Shop Stock Photo */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-5 h-5 text-teal-600" />
                      <h5 className="font-semibold text-gray-900">Shop Stock Photo</h5>
                    </div>
                    
                    {loan.documents?.shopStockPhoto ? (
                      <div className="space-y-2">
                        {loan.documents.shopStockPhoto.toLowerCase().includes('.pdf') ? (
                          <a
                            href={loan.documents.shopStockPhoto}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-48 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border-2 border-teal-200 hover:border-teal-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                          >
                            <FileText className="w-12 h-12 text-teal-600" />
                            <span className="text-sm font-medium text-teal-900">View PDF</span>
                          </a>
                        ) : (
                          <a
                            href={loan.documents.shopStockPhoto}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={loan.documents.shopStockPhoto}
                              alt="Shop Stock Photo"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-teal-500 transition-colors cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-sm">Shop Stock Photo not available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-semibold text-sm text-gray-900 mb-3">Additional Information</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <p className="text-gray-500">Full Name</p>
                    <p className="font-medium text-gray-900">{loan.fullName || loan.businessName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{loan.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Mobile</p>
                    <p className="font-medium text-gray-900">{loan.mobileNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">GST Number</p>
                    <p className="font-medium text-gray-900">{loan.gstNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bank Account</p>
                    <p className="font-medium text-gray-900">{loan.bankAccountNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">IFSC Code</p>
                    <p className="font-medium text-gray-900">{loan.ifscCode || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
              <button
                onClick={handleDownloadDocuments}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {messageModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Send Message</h3>
              <button
                onClick={() => setMessageModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={sendingMessage}
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send to: {loan.fullName || loan.businessName || 'Customer'}
                </label>
                <p className="text-xs text-gray-500">{loan.email || 'No email available'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={sendingMessage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={sendingMessage}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button
                onClick={() => setMessageModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sendingMessage}
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={sendingMessage}
              >
                {sendingMessage && <RefreshCw className="w-4 h-4 animate-spin" />}
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {showActivityLog && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Activity Log</h3>
              <button
                onClick={() => setShowActivityLog(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Application Submitted</p>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(loan.applicationDate)}</p>
                  </div>
                </div>
                {loan.updatedAt && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Status Updated</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Updated to: <span className="font-semibold capitalize">{loan.status?.replace('-', ' ')}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(loan.updatedAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setShowActivityLog(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-blue-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Confirm Action
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to <span className="font-semibold">{confirmModal.action}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, action: '', status: '' })}
                  className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmStatusUpdate}
                  disabled={updating}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updating ? 'Processing...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
